/**
 * Mobile numbers are stored as E.164 ("+18069995417"). The web profile shows
 * them read-only the way dashboard-app-mobile and admin-app-mobile do: the
 * country's flag and calling code, then the national number in the country's
 * own grouping. Ported from dashboard-app-mobile lib/core/phone/.
 */

export interface PhoneCountry {
  iso: string;
  name: string;
  /** Without the plus: "1", "94". */
  dialCode: string;
}

export interface ParsedPhone {
  country: PhoneCountry;
  /** Digits only, without the calling code. */
  national: string;
}

/** Shown in preference when a calling code is shared (+1 is US, CA and more). */
const COMMON_ISOS = ['US'];

// prettier-ignore
const COUNTRY_ROWS: [string, string, string][] = [
  ['AF', 'Afghanistan', '93'],
  ['AL', 'Albania', '355'],
  ['DZ', 'Algeria', '213'],
  ['AD', 'Andorra', '376'],
  ['AO', 'Angola', '244'],
  ['AR', 'Argentina', '54'],
  ['AM', 'Armenia', '374'],
  ['AW', 'Aruba', '297'],
  ['AU', 'Australia', '61'],
  ['AT', 'Austria', '43'],
  ['AZ', 'Azerbaijan', '994'],
  ['BS', 'Bahamas', '1'],
  ['BH', 'Bahrain', '973'],
  ['BD', 'Bangladesh', '880'],
  ['BB', 'Barbados', '1'],
  ['BY', 'Belarus', '375'],
  ['BE', 'Belgium', '32'],
  ['BZ', 'Belize', '501'],
  ['BJ', 'Benin', '229'],
  ['BM', 'Bermuda', '1'],
  ['BT', 'Bhutan', '975'],
  ['BO', 'Bolivia', '591'],
  ['BA', 'Bosnia and Herzegovina', '387'],
  ['BW', 'Botswana', '267'],
  ['BR', 'Brazil', '55'],
  ['BN', 'Brunei', '673'],
  ['BG', 'Bulgaria', '359'],
  ['BF', 'Burkina Faso', '226'],
  ['BI', 'Burundi', '257'],
  ['KH', 'Cambodia', '855'],
  ['CM', 'Cameroon', '237'],
  ['CA', 'Canada', '1'],
  ['CV', 'Cape Verde', '238'],
  ['KY', 'Cayman Islands', '1'],
  ['CF', 'Central African Republic', '236'],
  ['TD', 'Chad', '235'],
  ['CL', 'Chile', '56'],
  ['CN', 'China', '86'],
  ['CO', 'Colombia', '57'],
  ['CG', 'Congo', '242'],
  ['CD', 'Congo (DRC)', '243'],
  ['CR', 'Costa Rica', '506'],
  ['CI', 'Côte d’Ivoire', '225'],
  ['HR', 'Croatia', '385'],
  ['CU', 'Cuba', '53'],
  ['CY', 'Cyprus', '357'],
  ['CZ', 'Czechia', '420'],
  ['DK', 'Denmark', '45'],
  ['DJ', 'Djibouti', '253'],
  ['DO', 'Dominican Republic', '1'],
  ['EC', 'Ecuador', '593'],
  ['EG', 'Egypt', '20'],
  ['SV', 'El Salvador', '503'],
  ['EE', 'Estonia', '372'],
  ['ET', 'Ethiopia', '251'],
  ['FJ', 'Fiji', '679'],
  ['FI', 'Finland', '358'],
  ['FR', 'France', '33'],
  ['GA', 'Gabon', '241'],
  ['GM', 'Gambia', '220'],
  ['GE', 'Georgia', '995'],
  ['DE', 'Germany', '49'],
  ['GH', 'Ghana', '233'],
  ['GR', 'Greece', '30'],
  ['GD', 'Grenada', '1'],
  ['GT', 'Guatemala', '502'],
  ['GN', 'Guinea', '224'],
  ['GY', 'Guyana', '592'],
  ['HT', 'Haiti', '509'],
  ['HN', 'Honduras', '504'],
  ['HK', 'Hong Kong', '852'],
  ['HU', 'Hungary', '36'],
  ['IS', 'Iceland', '354'],
  ['IN', 'India', '91'],
  ['ID', 'Indonesia', '62'],
  ['IR', 'Iran', '98'],
  ['IQ', 'Iraq', '964'],
  ['IE', 'Ireland', '353'],
  ['IL', 'Israel', '972'],
  ['IT', 'Italy', '39'],
  ['JM', 'Jamaica', '1'],
  ['JP', 'Japan', '81'],
  ['JO', 'Jordan', '962'],
  ['KZ', 'Kazakhstan', '7'],
  ['KE', 'Kenya', '254'],
  ['KW', 'Kuwait', '965'],
  ['KG', 'Kyrgyzstan', '996'],
  ['LA', 'Laos', '856'],
  ['LV', 'Latvia', '371'],
  ['LB', 'Lebanon', '961'],
  ['LS', 'Lesotho', '266'],
  ['LR', 'Liberia', '231'],
  ['LY', 'Libya', '218'],
  ['LI', 'Liechtenstein', '423'],
  ['LT', 'Lithuania', '370'],
  ['LU', 'Luxembourg', '352'],
  ['MO', 'Macao', '853'],
  ['MG', 'Madagascar', '261'],
  ['MW', 'Malawi', '265'],
  ['MY', 'Malaysia', '60'],
  ['MV', 'Maldives', '960'],
  ['ML', 'Mali', '223'],
  ['MT', 'Malta', '356'],
  ['MR', 'Mauritania', '222'],
  ['MU', 'Mauritius', '230'],
  ['MX', 'Mexico', '52'],
  ['MD', 'Moldova', '373'],
  ['MC', 'Monaco', '377'],
  ['MN', 'Mongolia', '976'],
  ['ME', 'Montenegro', '382'],
  ['MA', 'Morocco', '212'],
  ['MZ', 'Mozambique', '258'],
  ['MM', 'Myanmar', '95'],
  ['NA', 'Namibia', '264'],
  ['NP', 'Nepal', '977'],
  ['NL', 'Netherlands', '31'],
  ['NZ', 'New Zealand', '64'],
  ['NI', 'Nicaragua', '505'],
  ['NE', 'Niger', '227'],
  ['NG', 'Nigeria', '234'],
  ['KP', 'North Korea', '850'],
  ['MK', 'North Macedonia', '389'],
  ['NO', 'Norway', '47'],
  ['OM', 'Oman', '968'],
  ['PK', 'Pakistan', '92'],
  ['PS', 'Palestine', '970'],
  ['PA', 'Panama', '507'],
  ['PG', 'Papua New Guinea', '675'],
  ['PY', 'Paraguay', '595'],
  ['PE', 'Peru', '51'],
  ['PH', 'Philippines', '63'],
  ['PL', 'Poland', '48'],
  ['PT', 'Portugal', '351'],
  ['PR', 'Puerto Rico', '1'],
  ['QA', 'Qatar', '974'],
  ['RO', 'Romania', '40'],
  ['RU', 'Russia', '7'],
  ['RW', 'Rwanda', '250'],
  ['SA', 'Saudi Arabia', '966'],
  ['SN', 'Senegal', '221'],
  ['RS', 'Serbia', '381'],
  ['SC', 'Seychelles', '248'],
  ['SL', 'Sierra Leone', '232'],
  ['SG', 'Singapore', '65'],
  ['SK', 'Slovakia', '421'],
  ['SI', 'Slovenia', '386'],
  ['SO', 'Somalia', '252'],
  ['ZA', 'South Africa', '27'],
  ['KR', 'South Korea', '82'],
  ['SS', 'South Sudan', '211'],
  ['ES', 'Spain', '34'],
  ['LK', 'Sri Lanka', '94'],
  ['SD', 'Sudan', '249'],
  ['SE', 'Sweden', '46'],
  ['CH', 'Switzerland', '41'],
  ['SY', 'Syria', '963'],
  ['TW', 'Taiwan', '886'],
  ['TJ', 'Tajikistan', '992'],
  ['TZ', 'Tanzania', '255'],
  ['TH', 'Thailand', '66'],
  ['TG', 'Togo', '228'],
  ['TT', 'Trinidad and Tobago', '1'],
  ['TN', 'Tunisia', '216'],
  ['TR', 'Türkiye', '90'],
  ['TM', 'Turkmenistan', '993'],
  ['UG', 'Uganda', '256'],
  ['UA', 'Ukraine', '380'],
  ['AE', 'United Arab Emirates', '971'],
  ['GB', 'United Kingdom', '44'],
  ['US', 'United States', '1'],
  ['UY', 'Uruguay', '598'],
  ['UZ', 'Uzbekistan', '998'],
  ['VE', 'Venezuela', '58'],
  ['VN', 'Vietnam', '84'],
  ['YE', 'Yemen', '967'],
  ['ZM', 'Zambia', '260'],
  ['ZW', 'Zimbabwe', '263'],
];

export const ALL_COUNTRIES: PhoneCountry[] = COUNTRY_ROWS.map(([iso, name, dialCode]) => ({
  iso,
  name,
  dialCode,
}));

// Longest calling code first, as mobile does, so a short code never shadows a longer one.
const BY_DIAL_CODE_LENGTH = [...ALL_COUNTRIES].sort(
  (a, b) => b.dialCode.length - a.dialCode.length,
);

export function countryOf(iso: string): PhoneCountry {
  return ALL_COUNTRIES.find((c) => c.iso === iso) ?? ALL_COUNTRIES.find((c) => c.iso === 'US')!;
}

/** 🇺🇸 from "US". Regional indicators sit 0x1F1A5 above ASCII capitals. */
export function flagOf(country: PhoneCountry): string {
  return String.fromCodePoint(
    ...[...country.iso.toUpperCase()].map((ch) => ch.charCodeAt(0) + 0x1f1a5),
  );
}

function preferredFor(dialCode: string, fallback: PhoneCountry): PhoneCountry {
  const sharing = ALL_COUNTRIES.filter((c) => c.dialCode === dialCode);
  for (const iso of COMMON_ISOS) {
    const match = sharing.find((c) => c.iso === iso);
    if (match) return match;
  }
  return fallback;
}

/**
 * Reads back what the service stored, in any shape it holds. "+94771234567"
 * resolves by calling code; a bare ten-digit number (written before E.164)
 * is North American.
 */
export function parsePhone(stored: string | null | undefined, fallbackIso = 'US'): ParsedPhone {
  const cleaned = (stored ?? '').replace(/[^0-9+]/g, '');
  if (!cleaned) return { country: countryOf(fallbackIso), national: '' };

  if (cleaned.startsWith('+')) {
    const digits = cleaned.slice(1);
    const match = BY_DIAL_CODE_LENGTH.find((c) => digits.startsWith(c.dialCode));
    if (match) {
      return {
        country: preferredFor(match.dialCode, match),
        national: digits.slice(match.dialCode.length),
      };
    }
    return { country: countryOf(fallbackIso), national: digits };
  }

  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return { country: countryOf('US'), national: cleaned.slice(1) };
  }
  return { country: countryOf(fallbackIso), national: cleaned };
}

function groupingOf(country: PhoneCountry): number[] {
  switch (country.iso) {
    case 'US':
    case 'CA':
      return [3, 3, 4];
    case 'LK':
      return [2, 3, 4];
    default:
      return [];
  }
}

/** "(806) 999-5417" for North America, "77 123 4567" for Sri Lanka. */
export function formatNational(country: PhoneCountry, digits: string): string {
  const grouping = groupingOf(country);
  if (!grouping.length || !digits) return digits;

  const parts: string[] = [];
  let index = 0;
  for (const size of grouping) {
    if (index >= digits.length) break;
    const end = Math.min(index + size, digits.length);
    parts.push(digits.slice(index, end));
    index = end;
  }
  if (index < digits.length) parts.push(digits.slice(index));

  if (country.iso === 'US' || country.iso === 'CA') {
    if (parts.length === 1) return `(${parts[0]}`;
    if (parts.length === 2) return `(${parts[0]}) ${parts[1]}`;
    return `(${parts[0]}) ${parts[1]}-${parts.slice(2).join('')}`;
  }
  return parts.join(' ');
}
